'use client';

import Image from 'next/image';
import { motion } from 'motion/react';
import { SectionLayout } from '@/components/shared/section-layout';
import { Marquee } from '@/components/sections/trust-marquee/marquee';
import SectionHeader from '@/components/shared/section-header';
import { SanityInlineText, sanityTextToPlainText } from '@/components/shared/sanity-text';
import { SectionCtaPair, sectionCtaPairFromContent } from '@/components/shared/section-cta-pair';
import { useSectionMotionVariants } from '@/components/shared/motion';
import { useScrollRevealMotion } from '@/hooks/use-scroll-reveal-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import type { TrustMarqueeSectionContent } from '@/sanity/lib/sections';

export default function ClientMarquee({
  content,
}: {
  content?: TrustMarqueeSectionContent | null;
}) {
  const isMobile = useIsMobile();
  const { sectionSequenceBlockVariants, sectionSequenceRevealVariants } =
    useSectionMotionVariants();
  const scrollReveal = useScrollRevealMotion();
  if (!content || content.show === false) return null;

  const logos =
    content?.showTrustMarquee === false
      ? []
      : (content?.logos ?? []).flatMap((logo) => {
          const name = logo.alt || logo.name;
          if (!logo.src || !name) return [];

          return [
            {
              src: logo.src,
              name,
            },
          ];
        });

  const showMarqueeBlock = content.showTrustMarquee !== false && logos.length > 0;
  const { primary, secondary } = sectionCtaPairFromContent(content.showCTA, content.ctaButtons);
  const { header } = content;
  const subheading = header?.subheading?.trim() ?? '';
  const headingPlain = sanityTextToPlainText(header?.heading).trim();
  const hasHeadingBlocks = Boolean(header?.heading?.length);
  const hasRenderableHeading = Boolean(headingPlain || hasHeadingBlocks);
  const hasTopHeader = Boolean(
    content.showHeader !== false && (header?.tagline?.trim() || hasRenderableHeading),
  );
  const hasSubheading = Boolean(content.showHeader !== false && subheading);
  const hasHeaderBody = Boolean(hasTopHeader || hasSubheading);

  if (!showMarqueeBlock && !hasHeaderBody && !primary) return null;

  return (
    <SectionLayout
      className="relative bg-neutral-950 dark:bg-black dark:border-t py-6 sm:py-12 xl:py-16"
      containerClassName="mx-auto max-w-7xl px-4 sm:px-6 min-[1324px]:px-0"
    >
      <motion.div className="space-y-6" {...scrollReveal} variants={sectionSequenceRevealVariants}>
        {hasTopHeader ? (
          <SectionHeader
            sequenceChild
            className="text-left sm:text-center lg:max-w-3xl lg:mx-auto"
            content={{
              eyebrow: header?.tagline,
            }}
            titleSlotClassName="text-white dark:text-white"
            titleSlot={
              hasRenderableHeading ? (
                <h2 className="text-sm sm:text-base leading-relaxed font-light tracking-tight text-pretty text-white dark:text-white">
                  {hasHeadingBlocks ? (
                    <SanityInlineText
                      blocks={header?.heading}
                      emphasisClassName="italic font-normal text-white"
                      strongClassName="font-medium text-white"
                    />
                  ) : (
                    headingPlain
                  )}
                </h2>
              ) : undefined
            }
            eyebrowClassName="text-[#FCFAD7]/80 dark:text-[#FCFAD7]/80"
          />
        ) : null}
        {primary ? (
          <motion.div variants={sectionSequenceBlockVariants}>
            <SectionCtaPair
              primary={primary}
              secondary={secondary}
              trackingLocation="trust_marquee"
              className={cn(hasTopHeader && 'mt-6')}
            />
          </motion.div>
        ) : null}
        {showMarqueeBlock ? (
          <motion.div variants={sectionSequenceBlockVariants} className="relative">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-linear-to-r from-neutral-950 to-transparent sm:w-24"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-linear-to-l from-neutral-950 to-transparent sm:w-24"
            />
            <Marquee className={isMobile ? '[--duration:90s]' : '[--duration:60s]'}>
              {logos.map((logo) => (
                <div
                  key={logo.src}
                  className="flex h-20 w-44 shrink-0 items-center justify-center rounded-sm border-none bg-neutral-900 px-3"
                  title={logo.name}
                >
                  <Image
                    src={logo.src}
                    alt={logo.name}
                    width={140}
                    height={40}
                    className={cn(
                      'max-h-9 w-auto opacity-65 transition-opacity duration-300 hover:opacity-100 invert',
                      content.grayscale !== false && 'grayscale',
                    )}
                  />
                </div>
              ))}
            </Marquee>
          </motion.div>
        ) : null}
        {hasSubheading ? (
          <motion.p
            variants={sectionSequenceBlockVariants}
            className="text-left text-xs font-light leading-relaxed text-pretty text-neutral-400 sm:mt-8 sm:text-center sm:text-sm lg:max-w-3xl lg:mx-auto"
          >
            {subheading}
          </motion.p>
        ) : null}
      </motion.div>
    </SectionLayout>
  );
}
